import { MutableRefObject, createContext, useContext } from 'react';

import { TBedrockAgentRefineMode, TBedrockAgentType, TFeatureConfigSchema } from '@cloud-wave/neon-common-lib';

import { ISoftphoneModalOptionsProps } from '../SoftphoneModalContext/Context';
import { TActionID } from './components/IQActionsMenu/constants/IQActionItems';
import { IQResponseState, TIQState } from './components/constants/IQResponseState';
import { TIQActionFetchResponse, TIQFetchParams } from './types/iqActionFetch';
import { TStreamingState } from './types/contextAndHookTypes';

export type TReplaceTextCallback = (text: string) => void;

type TThink = (params: TIQFetchParams) => void;
type TRethink = () => void;
type TRetryFailedRequest = () => void;

export type TAiContext = {
  state: {
    IQEnabled: boolean;
    IQFeaturesEnabled: TBedrockAgentType[];
    IQFeatureConfig: TFeatureConfigSchema['genAI'];
    isActionMenuOpen: boolean;
    selectedAction: TActionID | null;
    iqInput: string | null;
    selectedMode: TBedrockAgentRefineMode | null;
    isIQModalOpen: boolean;
    replaceTextCallbackRef: MutableRefObject<TReplaceTextCallback | undefined>;
    requestState: TIQState;
    isStreaming: boolean;
    response: TIQActionFetchResponse | null;
    streamingState: TStreamingState;
  };
  actions: {
    openActionMenu: () => void;
    closeActionMenu: () => void;
    selectAction: (item: TActionID | null) => void;
    setIQInput: (text: string | null) => void;
    selectMode: (mode: TBedrockAgentRefineMode | null) => void;
    resetIQActionMenuState: () => void;
    openIQModal: (options?: ISoftphoneModalOptionsProps) => void;
    closeIQModal: () => void;
    resetIQApiState: () => void;
    abortActiveRequest: () => void;
    think: TThink;
    rethink: TRethink;
    retryFailedRequest: TRetryFailedRequest;
  };
};

const Context = createContext<TAiContext>({
  state: {
    IQEnabled: false,
    IQFeaturesEnabled: [],
    IQFeatureConfig: {},
    isActionMenuOpen: false,
    selectedAction: null,
    iqInput: null,
    selectedMode: null,
    isIQModalOpen: false,
    replaceTextCallbackRef: { current: () => undefined },
    requestState: IQResponseState.STANDBY,
    isStreaming: false,
    response: null,
    streamingState: {},
  },
  actions: {
    openActionMenu: () => void 0,
    closeActionMenu: () => void 0,
    selectAction: () => void 0,
    setIQInput: () => void 0,
    selectMode: () => void 0,
    resetIQActionMenuState: () => void 0,
    openIQModal: () => void 0,
    closeIQModal: () => void 0,
    resetIQApiState: () => void 0,
    abortActiveRequest: () => void 0,
    think: () => void 0,
    rethink: () => void 0,
    retryFailedRequest: () => void 0,
  },
});

export default Context;

export const useAIContext = () => useContext(Context);
